/**
 * IFRAP-AI.fr - Compteurs Temps Réel (Enhanced)
 * 
 * Features:
 * - Real-time counters updating at 20fps
 * - Full-screen TV mode with IFRAP logo
 * - Time period toggle: Temps réel / Jour / 30 jours / Année
 * - Decimal precision on millions
 * - Click for detailed breakdown (today, MTD, QTD, YTD, N-1 comparison)
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Maximize2, TrendingUp, TrendingDown, Minus, ChevronDown, ChevronUp, Monitor, Share2, ExternalLink } from 'lucide-react';

// Base annual figures (updated from official sources 2025)
const LIVE_COUNTERS_CONFIG = {
  depense_publique: {
    label: 'Dépenses publiques',
    annualAmount: 1_740_000_000_000,
    annualAmountN1: 1_667_000_000_000,
    unit: '\u20ac',
    descriptions: {
      realtime: 'dépensés depuis minuit',
      day: 'dépensés par jour',
      '30days': 'dépensés sur 30 jours',
      year: 'dépensés par an',
    },
    color: '#000091',
    source: 'INSEE Comptes nationaux 2025',
    icon: 'building',
    dataDate: '2025-12-31',
    dataPeriod: 'Année 2025',
  },
  dette_seconde: {
    label: 'Endettement net',
    annualAmount: 154_000_000_000,
    annualAmountN1: 150_000_000_000,
    unit: '\u20ac',
    descriptions: {
      realtime: 'de dette nouvelle depuis minuit',
      day: 'de dette nouvelle par jour',
      '30days': 'de dette nouvelle sur 30 jours',
      year: 'de dette nouvelle par an',
    },
    color: '#E1000F',
    source: 'INSEE T4 2025',
    icon: 'trending-up',
    dataDate: '2025-12-31',
    dataPeriod: 'Année 2025',
  },
  deficit_commercial: {
    label: 'Déficit commercial',
    annualAmount: 69_200_000_000,  // MISE À JOUR : 69,2 Mds€ (Douanes 2025)
    annualAmountN1: 79_200_000_000, // 2024
    unit: '\u20ac',
    descriptions: {
      realtime: "d'importations nettes depuis minuit",
      day: "d'importations nettes par jour",
      '30days': "d'importations nettes sur 30 jours",
      year: "d'importations nettes par an",
    },
    color: '#B45309',
    source: 'Douanes françaises 2025',
    icon: 'truck',
    dataDate: '2025-12-31', // Année 2025 complète
    dataPeriod: 'Année 2025',
  },
  eloignements: {
    label: 'Éloignements (OQTF)',
    annualAmount: 32_379,  // 2025 : 32 379 éloignements exécutés (+16,5%)
    annualAmountN1: 27_807, // 2024
    unit: '',
    descriptions: {
      realtime: "personnes éloignées depuis le 1er janvier",
      day: "éloignements par jour en moyenne",
      '30days': "éloignements sur 30 jours",
      year: "éloignements par an",
    },
    color: '#6366F1',
    source: 'Ministère de l\'Intérieur 2025',
    icon: 'plane-departure',
    dataDate: '2025-12-31',
    dataPeriod: 'Année 2025',
    isCount: true, // Not currency - show as count
  },
};

// Calculate data age in months and days
const calculateDataAge = (dateStr) => {
  if (!dateStr) return null;
  const dataDate = new Date(dateStr);
  const now = new Date();
  const diffMs = now - dataDate;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const months = Math.floor(diffDays / 30);
  const days = diffDays % 30;
  return { months, days, totalDays: diffDays };
};

// Get data periods for display
const getDataPeriods = () => {
  const periods = [];
  Object.values(LIVE_COUNTERS_CONFIG).forEach(config => {
    if (config.dataPeriod && !periods.includes(config.dataPeriod)) {
      periods.push(config.dataPeriod);
    }
  });
  return periods;
};

// Data Period Info Component (shows data sources period, not "outdated" warning)
const DataAgeAlertInline = () => {
  const periods = getDataPeriods();
  
  if (periods.length === 0) return null;
  
  // All same period
  const uniquePeriod = periods.length === 1 ? periods[0] : null;
  
  return (
    <div 
      className="inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium bg-blue-100 border border-blue-200 text-blue-700"
      data-testid="data-age-alert-inline"
    >
      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      {uniquePeriod ? (
        <span>Données <strong>{uniquePeriod}</strong></span>
      ) : (
        <span>Données <strong>{periods.join(' / ')}</strong></span>
      )}
    </div>
  );
};

const TIME_PERIODS = [
  { key: 'realtime', label: 'Temps réel', icon: null },
  { key: 'day', label: "Aujourd'hui" },
  { key: '30days', label: '30 jours' },
  { key: 'year', label: 'Année' },
];

// Calculate amount for a given period
const getAmountForPeriod = (config, period) => {
  const ratePerMs = config.annualAmount / (365 * 24 * 3600 * 1000);
  const now = new Date();
  const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (period) {
    case 'realtime': {
      const elapsed = Date.now() - midnight.getTime();
      return elapsed * ratePerMs;
    }
    case 'day': {
      return 24 * 3600 * 1000 * ratePerMs;
    }
    case '30days': {
      return 30 * 24 * 3600 * 1000 * ratePerMs;
    }
    case 'year': {
      return config.annualAmount;
    }
    default:
      return 0;
  }
};

const getAmountN1ForPeriod = (config, period) => {
  const ratePerMs = config.annualAmountN1 / (365 * 24 * 3600 * 1000);
  switch (period) {
    case 'realtime':
    case 'day':
      return 24 * 3600 * 1000 * ratePerMs;
    case '30days':
      return 30 * 24 * 3600 * 1000 * ratePerMs;
    case 'year':
      return config.annualAmountN1;
    default:
      return 0;
  }
};

// Format number with high precision decimals for millions
const formatNumberPrecise = (num, showDecimals = true) => {
  if (num >= 1_000_000_000) {
    const mds = num / 1_000_000_000;
    return showDecimals ? `${mds.toFixed(4)} milliards` : `${mds.toFixed(2)} milliards`;
  }
  if (num >= 1_000_000) {
    const m = num / 1_000_000;
    return showDecimals ? `${m.toFixed(4)} millions` : `${m.toFixed(1)} millions`;
  }
  if (num >= 1_000) return `${Math.floor(num).toLocaleString('fr-FR')}`;
  return `${num.toFixed(2)}`;
};

const formatNumberTV = (num) => {
  if (num >= 1_000_000_000) {
    return `${(num / 1_000_000_000).toFixed(6)} milliards`;
  }
  if (num >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(4)} millions`;
  }
  return `${Math.floor(num).toLocaleString('fr-FR')}`;
};

const formatCompact = (num) => {
  if (num >= 1_000_000_000) return `${(num / 1_000_000_000).toFixed(1)} milliards`;
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)} millions`;
  return `${Math.floor(num).toLocaleString('fr-FR')}`;
};

const formatPerSecond = (annual, isCount = false) => {
  const perSec = annual / (365 * 24 * 3600);
  if (isCount) {
    // For counts, show per day instead of per second
    const perDay = annual / 365;
    return `${perDay.toFixed(1)}/jour`;
  }
  return `${Math.round(perSec).toLocaleString('fr-FR')} \u20ac/sec`;
};

// ============================================================================
// DETAIL PANEL (click on counter)
// ============================================================================
const CounterDetail = ({ config, onClose }) => {
  const now = new Date();
  const ratePerDay = config.annualAmount / 365;
  const dayOfYear = Math.floor((now - new Date(now.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
  const month = now.getMonth();
  const quarterMonth = Math.floor(month / 3) * 3;
  const unit = config.isCount ? '' : '\u20ac';

  const today = ratePerDay;
  const mtd = ratePerDay * now.getDate();
  const qtd = ratePerDay * (dayOfYear - Math.floor((new Date(now.getFullYear(), quarterMonth, 1) - new Date(now.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24)));
  const ytd = ratePerDay * dayOfYear;

  const ratePerDayN1 = config.annualAmountN1 / 365;
  const todayN1 = ratePerDayN1;
  const mtdN1 = ratePerDayN1 * now.getDate();
  const qtdN1 = ratePerDayN1 * (dayOfYear - Math.floor((new Date(now.getFullYear(), quarterMonth, 1) - new Date(now.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24)));
  const ytdN1 = ratePerDayN1 * dayOfYear;

  const rows = [
    { label: "Aujourd'hui", value: today, valueN1: todayN1 },
    { label: 'Mois en cours', value: mtd, valueN1: mtdN1 },
    { label: 'Trimestre en cours', value: qtd, valueN1: qtdN1 },
    { label: 'Depuis janvier', value: ytd, valueN1: ytdN1 },
    { label: 'Total annuel', value: config.annualAmount, valueN1: config.annualAmountN1 },
  ];

  return (
    <div
      className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 p-4"
      data-testid={`counter-detail-${config.label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-sm" style={{ color: config.color }}>{config.label}</h4>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <X className="w-4 h-4" />
        </button>
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-gray-100">
            <th className="text-left pb-2 text-gray-500 font-medium">Période</th>
            <th className="text-right pb-2 text-gray-500 font-medium">2025</th>
            <th className="text-right pb-2 text-gray-500 font-medium">N-1</th>
            <th className="text-right pb-2 text-gray-500 font-medium">Écart</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const diff = row.value - row.valueN1;
            const pct = row.valueN1 ? ((diff / row.valueN1) * 100) : 0;
            const isUp = diff > 0;
            return (
              <tr key={row.label} className="border-b border-gray-50">
                <td className="py-1.5 text-gray-700">{row.label}</td>
                <td className="py-1.5 text-right font-mono font-semibold" style={{ color: config.color }}>
                  {formatCompact(row.value)}{unit}
                </td>
                <td className="py-1.5 text-right font-mono text-gray-400">
                  {formatCompact(row.valueN1)}{unit}
                </td>
                <td className="py-1.5 text-right">
                  <span className={`inline-flex items-center gap-0.5 font-semibold ${isUp ? 'text-red-600' : 'text-green-600'}`}>
                    {isUp ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    {pct > 0 ? '+' : ''}{pct.toFixed(1)}%
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="text-[10px] text-gray-400 mt-2">{config.source}</p>
    </div>
  );
};

// ============================================================================
// SINGLE COUNTER COMPONENT
// ============================================================================
const SingleCounter = ({ config, period, isTV = false, onClick, showDetail = false, onCloseDetail }) => {
  const [amount, setAmount] = useState(0);
  const startRef = useRef(null);
  const ratePerMs = config.annualAmount / (365 * 24 * 3600 * 1000);
  
  // Get description based on period
  const description = config.descriptions ? config.descriptions[period] : config.description;

  useEffect(() => {
    if (period !== 'realtime') {
      setAmount(getAmountForPeriod(config, period));
      return;
    }

    const now = new Date();
    const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    startRef.current = midnight.getTime();

    const tick = () => {
      const elapsed = Date.now() - startRef.current;
      setAmount(elapsed * ratePerMs);
    };

    tick();
    const interval = setInterval(tick, 50);
    return () => clearInterval(interval);
  }, [ratePerMs, period, config]);

  if (isTV) {
    return (
      <div
        className="flex flex-col items-center justify-center cursor-pointer relative group"
        onClick={onClick}
        data-testid={`tv-counter-${config.label.toLowerCase().replace(/\s/g, '-')}`}
      >
        <span className="text-white/60 text-lg sm:text-xl uppercase tracking-widest mb-2 font-medium">
          {config.label}
        </span>
        <div className="flex items-baseline gap-2">
          <span
            className="font-bold tabular-nums tracking-tight text-white"
            style={{ fontSize: 'clamp(2.5rem, 6vw, 5rem)' }}
          >
            {formatNumberTV(amount)}
          </span>
          <span className="text-white/50 text-xl sm:text-2xl">{config.unit}</span>
        </div>
        <p className="text-white/40 text-sm mt-1">{description}</p>
        <div className="flex items-center gap-2 text-white/30 text-xs mt-2">
          <span className="inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse" />
          {formatPerSecond(config.annualAmount, config.isCount)}
        </div>

        {showDetail && (
          <div className="absolute top-full mt-4 bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-4 sm:p-5 w-[90vw] sm:w-auto sm:min-w-[400px] max-w-[95vw] z-50 left-1/2 -translate-x-1/2">
            <TVCounterDetail config={config} onClose={onCloseDetail} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-md transition-shadow cursor-pointer relative"
      onClick={onClick}
      data-testid={`live-counter-${config.label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <div
          className="w-2 h-2 rounded-full animate-pulse"
          style={{ backgroundColor: config.color }}
        />
        <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
          {config.label}
        </span>
      </div>

      <div className="flex items-baseline gap-1 mb-1">
        <span
          className="text-2xl sm:text-3xl font-bold tabular-nums tracking-tight"
          style={{ color: config.color }}
        >
          {formatNumberPrecise(amount, period === 'realtime')}
        </span>
        <span className="text-sm text-gray-400">{config.unit}</span>
      </div>

      <p className="text-xs text-gray-500 mb-2">{description}</p>

      <div className="flex items-center justify-between text-[11px] text-gray-400 border-t border-gray-100 pt-2 mt-2">
        <span className="flex items-center gap-1">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
          {formatPerSecond(config.annualAmount, config.isCount)}
        </span>
        <span>{config.source}</span>
      </div>

      {showDetail && <CounterDetail config={config} onClose={onCloseDetail} />}
    </div>
  );
};

// ============================================================================
// TV COUNTER DETAIL (white-on-dark variant)
// ============================================================================
const TVCounterDetail = ({ config, onClose }) => {
  const now = new Date();
  const ratePerDay = config.annualAmount / 365;
  const dayOfYear = Math.floor((now - new Date(now.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
  const ytd = ratePerDay * dayOfYear;
  const ratePerDayN1 = config.annualAmountN1 / 365;
  const ytdN1 = ratePerDayN1 * dayOfYear;
  const diff = ytd - ytdN1;
  const pct = ytdN1 ? ((diff / ytdN1) * 100) : 0;
  const unit = config.isCount ? '' : '\u20ac';

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-sm text-white">{config.label} - Détail</h4>
        <button onClick={onClose} className="text-white/40 hover:text-white">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <span className="text-white/50 text-xs">Depuis janvier 2025</span>
          <p className="text-white font-bold text-lg">{formatCompact(ytd)}{unit}</p>
        </div>
        <div>
          <span className="text-white/50 text-xs">Même période N-1</span>
          <p className="text-white/70 font-bold text-lg">{formatCompact(ytdN1)}{unit}</p>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-white/10 flex items-center gap-2">
        <span className={`font-bold ${pct > 0 ? 'text-red-400' : 'text-green-400'}`}>
          {pct > 0 ? '+' : ''}{pct.toFixed(1)}% vs N-1
        </span>
        {pct > 0 ? <TrendingUp className="w-4 h-4 text-red-400" /> : <TrendingDown className="w-4 h-4 text-green-400" />}
      </div>
    </div>
  );
};

// ============================================================================
// MAIN LIVE COUNTERS COMPONENT
// ============================================================================
const LiveCounters = () => {
  const [period, setPeriod] = useState('realtime');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [detailCounter, setDetailCounter] = useState(null);

  const handleCounterClick = (key) => {
    setDetailCounter(detailCounter === key ? null : key);
  };

  // Escape key to close fullscreen
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        setIsFullscreen(false);
        setDetailCounter(null);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  // Full-screen TV Mode
  if (isFullscreen) {
    return (
      <div
        className="fixed inset-0 z-[9999] bg-gradient-to-br from-[#0a0a2e] via-[#000020] to-[#0d0d3a] flex flex-col overflow-hidden"
        data-testid="tv-fullscreen-mode"
      >
        {/* Top bar - Logo + Close - RESPONSIVE */}
        <div className="flex items-center justify-between px-4 sm:px-8 py-3 sm:py-5 shrink-0">
          <div className="flex items-center gap-2 sm:gap-4">
            <div className="w-10 h-10 sm:w-14 sm:h-14 bg-white rounded-lg flex items-center justify-center shadow-lg">
              <span className="text-[#000091] font-black text-base sm:text-xl tracking-tight">IF</span>
            </div>
            <div className="hidden sm:block">
              <span className="text-white font-bold text-2xl tracking-tight">IFRAP</span>
              <span className="text-white/40 text-sm block">Observatoire en temps réel</span>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-4">
            {/* Time period toggle - hide labels on mobile */}
            <div className="flex bg-white/5 rounded-lg p-1 border border-white/10">
              {TIME_PERIODS.map((tp) => (
                <button
                  key={tp.key}
                  onClick={() => setPeriod(tp.key)}
                  className={`px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm rounded-md transition-all ${
                    period === tp.key
                      ? 'bg-[#E1000F] text-white font-semibold shadow-lg'
                      : 'text-white/50 hover:text-white hover:bg-white/5'
                  }`}
                  data-testid={`tv-period-${tp.key}`}
                >
                  <span className="hidden sm:inline">{tp.label}</span>
                  <span className="sm:hidden">{tp.key === 'realtime' ? '⚡' : tp.key === 'daily' ? '📅' : tp.key === 'monthly' ? '📆' : '📊'}</span>
                </button>
              ))}
            </div>

            {/* BOUTON RETOUR VISIBLE */}
            <button
              onClick={() => setIsFullscreen(false)}
              className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors border border-white/20"
              data-testid="tv-close-btn"
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-xs sm:text-sm font-medium">Retour</span>
            </button>
          </div>
        </div>

        {/* Counters - RESPONSIVE GRID */}
        <div className="flex-1 flex items-center justify-center overflow-auto py-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-12 px-4 sm:px-8 max-w-7xl w-full">
            {Object.entries(LIVE_COUNTERS_CONFIG).map(([key, config]) => (
              <SingleCounter
                key={key}
                config={config}
                period={period}
                isTV={true}
                onClick={() => handleCounterClick(key)}
                showDetail={detailCounter === key}
                onCloseDetail={(e) => { e?.stopPropagation?.(); setDetailCounter(null); }}
              />
            ))}
          </div>
        </div>

        {/* Bottom bar - RESPONSIVE */}
        <div className="flex flex-col sm:flex-row items-center justify-between px-4 sm:px-8 py-3 sm:py-4 border-t border-white/5 gap-2 shrink-0">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            <span className="text-white/30 text-[10px] sm:text-xs text-center sm:text-left">
              Données calculées en temps réel à partir des chiffres annuels officiels
            </span>
          </div>
          <span className="text-white/20 text-[10px] sm:text-xs">
            Sources : Eurostat, INSEE, Douanes
          </span>
        </div>
      </div>
    );
  }

  // Normal inline mode
  return (
    <section className="py-6 md:py-8 bg-gray-50 border-t border-b border-gray-200" data-testid="live-counters-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header - Stack on mobile */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          {/* Title + Data Age Alert */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <div className="flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <h2 className="text-sm sm:text-base font-semibold text-gray-800">
                Compteurs temps réel
              </h2>
              <span className="text-[10px] sm:text-xs text-gray-400 hidden sm:inline">Données annuelles officielles</span>
            </div>
            <DataAgeAlertInline />
          </div>

          {/* Controls */}
          <div className="flex items-center gap-2">
            {/* Time period toggle - scrollable on mobile */}
            <div className="flex bg-gray-100 rounded-md p-0.5 border border-gray-200 overflow-x-auto">
              {TIME_PERIODS.map((tp) => (
                <button
                  key={tp.key}
                  onClick={() => setPeriod(tp.key)}
                  className={`px-2 sm:px-3 py-1 text-[10px] sm:text-xs rounded transition-all whitespace-nowrap ${
                    period === tp.key
                      ? 'bg-[#000091] text-white font-semibold shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid={`period-${tp.key}`}
                >
                  {tp.label}
                </button>
              ))}
            </div>

            {/* Fullscreen button */}
            <button
              onClick={() => setIsFullscreen(true)}
              className="flex items-center gap-1 px-2 sm:px-3 py-1 sm:py-1.5 bg-[#000091] text-white text-[10px] sm:text-xs font-medium rounded hover:bg-[#000091]/90 transition-colors whitespace-nowrap"
              data-testid="fullscreen-counter-btn"
            >
              <Maximize2 className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
              <span>Plein écran</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
          {Object.entries(LIVE_COUNTERS_CONFIG).map(([key, config]) => (
            <SingleCounter
              key={key}
              config={config}
              period={period}
              onClick={() => handleCounterClick(key)}
              showDetail={detailCounter === key}
              onCloseDetail={() => setDetailCounter(null)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default LiveCounters;
